import { ErrorBoundary } from '@sentry/react'
import type React from 'react'

// A single top-level error boundary, placed ABOVE every provider in App.tsx.
//
// The blank pages we couldn't see were the whole React tree unmounting on an
// uncaught render throw. The most fragile thing rendering at the root is the web3
// / wallet provider stack (WagmiProvider, fed by appkit.ts), which fails in
// restricted environments such as the in-app browser an email client opens links
// in. A boundary only catches throws from components *below* it, so to catch a
// provider blowing up it has to sit outside the providers.
//
// Because it's above the providers, the fallback cannot use Chakra, Mantine,
// wagmi or the router — none of them are mounted here. It is therefore deliberately
// plain markup with inline styles (so it can never itself render blank) and a hard
// reload rather than a client-side navigation. Sentry.ErrorBoundary reports the
// error itself and hands back the eventId, which we show as a reference so a
// support report can be tied to the captured event.
//
// Limitation worth stating: a React error boundary cannot catch errors thrown at
// *module evaluation* time (e.g. appkit.ts's createAppKit() throwing on import) —
// those happen before any component renders. That path is guarded separately (a
// try/catch in appkit.ts) and reported to the same Sentry project, and Sentry is
// now initialised early enough (see sentry.ts) that its global handlers catch it.
const AppErrorBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ErrorBoundary
    fallback={({ eventId }) => (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
          background: '#ffffff',
          color: '#111827',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <h1 style={{ fontSize: '22px', fontWeight: 600, margin: '0 0 8px' }}>
          Something went wrong
        </h1>
        <p
          style={{
            maxWidth: '28rem',
            margin: '0 0 24px',
            lineHeight: 1.5,
            color: '#4b5563',
          }}
        >
          This page didn't load correctly. Please reload to try again. If it keeps
          happening, contact{' '}
          <a
            href="mailto:support@penning.io"
            style={{ color: '#2563eb', textDecoration: 'underline' }}
          >
            support@penning.io
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            appearance: 'none',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 24px',
            background: '#2563eb',
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
        {eventId ? (
          <p style={{ fontSize: '12px', marginTop: '24px', color: '#9ca3af' }}>
            Reference: {eventId}
          </p>
        ) : null}
      </div>
    )}
  >
    {children}
  </ErrorBoundary>
)

export default AppErrorBoundary
